'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '@/lib/utils'

/**
 * Tabs — content section switcher.
 * Figma: Design System / Compositions / Tab item (242:1045) + TabStrip (242:1046).
 * Two visual variants: `underline` (default — bottom-border indicator)
 * and `pills` (filled brand-subtle background on active).
 */

type TabsVariant = 'underline' | 'pills'

const TabsVariantContext = React.createContext<TabsVariant>('underline')

const Tabs = TabsPrimitive.Root

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
    variant?: TabsVariant
}

const TabsList = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.List>,
    TabsListProps
>(({ className, variant = 'underline', children, ...props }, ref) => (
    <TabsVariantContext.Provider value={variant}>
        <TabsPrimitive.List
            ref={ref}
            className={cn(
                'inline-flex items-center',
                variant === 'underline' &&
                    'h-10 w-full justify-start gap-6 border-b border-[var(--border-subtle)]',
                variant === 'pills' &&
                    'h-10 gap-1 rounded-lg bg-[var(--bg-surface)] p-1 ring-1 ring-[var(--border-subtle)]',
                className,
            )}
            {...props}
        >
            {children}
        </TabsPrimitive.List>
    </TabsVariantContext.Provider>
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
    const variant = React.useContext(TabsVariantContext)
    return (
        <TabsPrimitive.Trigger
            ref={ref}
            className={cn(
                'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium leading-5 transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:text-[var(--text-disabled)]',
                variant === 'underline' && [
                    'relative h-10 px-4 text-[var(--text-secondary)]',
                    'hover:text-[var(--text-primary)]',
                    'data-[state=active]:text-[var(--text-primary)]',
                    'data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:-bottom-px data-[state=active]:after:h-0.5 data-[state=active]:after:bg-[var(--bg-brand)] data-[state=active]:after:rounded-full',
                ],
                variant === 'pills' && [
                    'h-8 rounded-md px-3 text-[var(--text-secondary)]',
                    "relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
                    'hover:text-[var(--text-primary)]',
                    // sage tint, text-safe sage-600+ only (500 fails AA as text)
                    'data-[state=active]:bg-[var(--sage-50)] data-[state=active]:text-[var(--text-sage)] data-[state=active]:ring-1 data-[state=active]:ring-[var(--sage-200)]',
                ],
                className,
            )}
            {...props}
        />
    )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            'mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2 rounded-md',
            className,
        )}
        {...props}
    />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
